/**
 * rsETH bridge routes.
 *
 * All rsETH bridging goes through Chainlink's Transporter (CCIP). The former
 * LayerZero/Stargate paths are retired — Stargate no longer lists rsETH at all.
 *
 * Two things about Transporter's URL format are easy to get wrong:
 *
 * 1. `token` is a double-URL-encoded base64 of "<chain>,<token address>", and
 *    it stays anchored to the ETHEREUM rsETH address in both directions. One
 *    constant therefore serves every route.
 * 2. The chain slugs are Transporter's own, not ours — Ethereum is "mainnet",
 *    not "ethereum". An unrecognised slug is silently discarded and the route
 *    simply arrives unselected, so a wrong value fails invisibly rather than
 *    erroring. These were read back from the live app.
 */

export const ETHEREUM_CHAIN_ID = 1;

/** base64("ethereum-mainnet,0xa1290d69c65a6fe4df752f95823fae25cb99e5a7"), double-URL-encoded. */
const TRANSPORTER_RSETH_TOKEN =
  "ZXRoZXJldW0tbWFpbm5ldCwweGExMjkwZDY5YzY1YTZmZTRkZjc1MmY5NTgyM2ZhZTI1Y2I5OWU1YTc%253D";

const ETHEREUM_SLUG = "mainnet";

const transporterUrl = (fromSlug: string, toSlug: string): string =>
  `https://app.transporter.io/?from=${fromSlug}&tab=token&to=${toSlug}&token=${TRANSPORTER_RSETH_TOKEN}`;

/**
 * How a chain's rsETH is moved.
 *
 * `transporter` is an external link to Chainlink's Transporter (CCIP).
 * `layerzero` is bridged natively in-app against the OFT contracts, because
 * LayerZero removed rsETH from their own UI and those holders would otherwise
 * have no path. Arbitrum is the only such chain, and is expected to become
 * `transporter` once it migrates to CCIP.
 */
export type BridgeMechanism = "transporter" | "layerzero";

export type RsEthBridgeRoute = {
  chainId: number;
  mechanism: BridgeMechanism;
  /** Transporter's slug for this chain. Unused when the mechanism is layerzero. */
  slug: string;
  /** Whether Ethereum → this chain is open. */
  entryOpen: boolean;
  /** Whether this chain → Ethereum is open. */
  exitOpen: boolean;
  /** Whether the chain is offered in the selectors at all. */
  enabled: boolean;
};

export const RSETH_BRIDGE_ROUTES: RsEthBridgeRoute[] = [
  {
    chainId: 8453,
    mechanism: "transporter",
    slug: "base",
    entryOpen: true,
    exitOpen: true,
    enabled: true,
  },
  {
    chainId: 59144,
    mechanism: "transporter",
    slug: "linea",
    entryOpen: true,
    exitOpen: true,
    enabled: true,
  },
  {
    chainId: 5000,
    mechanism: "transporter",
    slug: "mantle",
    entryOpen: true,
    exitOpen: true,
    enabled: true,
  },
  {
    chainId: 57073,
    mechanism: "transporter",
    slug: "ink",
    entryOpen: true,
    exitOpen: true,
    enabled: true,
  },
  // Exit only — bridging into Optimism is closed.
  {
    chainId: 10,
    mechanism: "transporter",
    slug: "optimism",
    entryOpen: false,
    exitOpen: true,
    enabled: true,
  },
  // Bridged natively in-app rather than linked out — LayerZero delisted rsETH
  // from their UI. Becomes a "transporter" row once Arbitrum moves to CCIP.
  {
    chainId: 42161,
    mechanism: "layerzero",
    slug: "arbitrum",
    entryOpen: true,
    exitOpen: true,
    enabled: true,
  },
];

const activeRoute = (chainId: number): RsEthBridgeRoute | undefined =>
  RSETH_BRIDGE_ROUTES.find((r) => r.chainId === chainId && r.enabled);

/**
 * The Transporter link for a single leg, or null when that direction is closed.
 * Only Ethereum↔chain legs exist; a chain-to-chain pair is two legs via Ethereum.
 */
export const getTransporterLink = (
  fromChainId: number,
  toChainId: number
): string | null => {
  if (fromChainId === toChainId) return null;

  if (fromChainId === ETHEREUM_CHAIN_ID) {
    const to = activeRoute(toChainId);
    if (!to || to.mechanism !== "transporter") return null;
    return to.entryOpen ? transporterUrl(ETHEREUM_SLUG, to.slug) : null;
  }

  if (toChainId === ETHEREUM_CHAIN_ID) {
    const from = activeRoute(fromChainId);
    if (!from || from.mechanism !== "transporter") return null;
    return from.exitOpen ? transporterUrl(from.slug, ETHEREUM_SLUG) : null;
  }

  // Neither side is Ethereum — the journey routes via Ethereum as two legs.
  return null;
};

/**
 * Whether a single Ethereum<->chain leg is open, independent of HOW it is
 * bridged. `getTransporterLink` answers "what link does this leg produce" and
 * is null for a LayerZero leg, which has no link — so selectability has to be
 * asked separately or Arbitrum would disappear from the selectors.
 */
export const isLegOpen = (fromChainId: number, toChainId: number): boolean => {
  if (fromChainId === toChainId) return false;
  if (fromChainId === ETHEREUM_CHAIN_ID) {
    return activeRoute(toChainId)?.entryOpen ?? false;
  }
  if (toChainId === ETHEREUM_CHAIN_ID) {
    return activeRoute(fromChainId)?.exitOpen ?? false;
  }
  return false;
};

/** Whether this leg is bridged natively in-app rather than linked out. */
export const isLayerZeroLeg = (
  fromChainId: number,
  toChainId: number
): boolean => {
  if (!isLegOpen(fromChainId, toChainId)) return false;
  const other = fromChainId === ETHEREUM_CHAIN_ID ? toChainId : fromChainId;
  return activeRoute(other)?.mechanism === "layerzero";
};

/** Chains that can be used as a bridge source (they can reach Ethereum). */
export const bridgeSourceChainIds = (): number[] =>
  RSETH_BRIDGE_ROUTES.filter((r) => r.enabled && r.exitOpen).map(
    (r) => r.chainId
  );

/** Chains that can be used as a bridge destination (Ethereum can reach them). */
export const bridgeDestinationChainIds = (): number[] =>
  RSETH_BRIDGE_ROUTES.filter((r) => r.enabled && r.entryOpen).map(
    (r) => r.chainId
  );

/**
 * Whether a full journey is possible, counting the two-leg route via Ethereum.
 * Same-chain is always allowed — that is the wrap-only journey.
 */
export const isBridgeableRoute = (
  fromChainId: number,
  toChainId: number
): boolean => {
  if (fromChainId === toChainId) return true;
  if (fromChainId === ETHEREUM_CHAIN_ID || toChainId === ETHEREUM_CHAIN_ID) {
    return isLegOpen(fromChainId, toChainId);
  }
  return (
    isLegOpen(fromChainId, ETHEREUM_CHAIN_ID) &&
    isLegOpen(ETHEREUM_CHAIN_ID, toChainId)
  );
};
